import React from 'react';

import { BRAND_COLORS } from './brandTokens';

export interface LensMarkProps {
  /** Size in px, applied to both width and height. Defaults to 24. */
  size?: number;
  /** Renders the ring in Compass White for placement on an Ocean Teal field. The dot stays Coral Green. */
  inverted?: boolean;
  className?: string;
}

/**
 * The Lens ring-dot: a saved live view, drawn on a 100-unit grid (ring
 * centred at 50, outer radius 30, stroke 10; dot radius 9). Never filled,
 * never inside a container, never a magnifying glass or an eye. The ring is
 * structural (Ocean Teal, Compass White when inverted); the dot is always
 * Coral Green, the one place this mark carries the accent colour.
 */
export default function LensMark({ size = 24, inverted = false, className }: LensMarkProps) {
  const ringColor = inverted ? BRAND_COLORS.compassWhite : BRAND_COLORS.oceanTeal;

  return (
    <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label="Lens" className={className}>
      <circle cx="50" cy="50" r="30" fill="none" stroke={ringColor} strokeWidth="10" />
      <circle cx="50" cy="50" r="9" fill={BRAND_COLORS.coralGreen} />
    </svg>
  );
}

/**
 * The highlighted chart marker: the same ring-dot device, hand-tuned for
 * legibility at marker scale (ring radius 9, stroke 3, dot radius 3.2). This
 * is not a proportional scale-down of the full mark, which would render the
 * Coral Green dot too small to read. Returns an ECharts-ready `image://`
 * symbol URL for a series' or markPoint's `symbol` field; pair it with a
 * `symbolSize` around 20 to 24.
 */
export function lensMarkerSymbol(inverted = false): string {
  const ringColor = inverted ? BRAND_COLORS.compassWhite : BRAND_COLORS.oceanTeal;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">` +
    `<circle cx="12" cy="12" r="9" fill="none" stroke="${ringColor}" stroke-width="3"/>` +
    `<circle cx="12" cy="12" r="3.2" fill="${BRAND_COLORS.coralGreen}"/>` +
    `</svg>`;
  return `image://data:image/svg+xml,${encodeURIComponent(svg)}`;
}
